import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, CheckCircle, ClipboardList, Printer, ArrowRight, Clock, MapPin, Hash, ChevronRight, RefreshCw } from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function FulfillmentPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [fulfillments, setFulfillments] = useState({ pending: [], picking: [], packing: [], shipped: [], delivered: [] });
  const [loading, setLoading] = useState(true);
  const [selectedFulfillment, setSelectedFulfillment] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const profile = await authService.getProfile();
      setUser(profile.member);
      const res = await apiClient.get('/api/v1/fulfillments');
      const items = res.data.fulfillments || [];
      const grouped = { pending: [], picking: [], packing: [], shipped: [], delivered: [] };
      items.forEach(f => { if (grouped[f.status]) grouped[f.status].push(f); });
      setFulfillments(grouped);
    } catch (err) {
      const demo = {
        pending: [
          { id: 'FUL-001', order_id: 'ORD-2026-001', items: [{ title: 'Wireless Headphones', qty: 1 }, { title: 'USB-C Cable', qty: 2 }], customer: 'Sarah Chen', carrier: null, tracking: null, created_at: '10 min ago', address: 'San Francisco, CA' },
          { id: 'FUL-002', order_id: 'ORD-2026-002', items: [{ title: 'Smart Sensor Kit', qty: 1 }], customer: 'Agent-7X9K', carrier: null, tracking: null, created_at: '25 min ago', address: 'Austin, TX' },
        ],
        picking: [
          { id: 'FUL-003', order_id: 'ORD-2026-006', items: [{ title: 'Coffee Blend x3', qty: 3 }], customer: 'Dave Kim', carrier: 'UPS', tracking: null, created_at: '45 min ago', address: 'Portland, OR', picker: 'Zone A-3' },
        ],
        packing: [
          { id: 'FUL-004', order_id: 'ORD-2026-007', items: [{ title: 'Running Shoes Ultra', qty: 1 }, { title: 'Athletic Socks 3pk', qty: 1 }], customer: 'Emily Ross', carrier: 'FedEx', tracking: null, created_at: '1 hr ago', address: 'Denver, CO', box_size: '12x10x6' },
        ],
        shipped: [
          { id: 'FUL-005', order_id: 'ORD-2026-003', items: [{ title: 'Bamboo Cutting Board', qty: 1 }], customer: 'Mike Johnson', carrier: 'UPS', tracking: 'UPS-1Z999AA10123456784', created_at: '2 hr ago', address: 'Denver, CO', eta: 'Mar 8' },
        ],
        delivered: [
          { id: 'FUL-006', order_id: 'ORD-2026-004', items: [{ title: 'Smart Home Hub Pro x3', qty: 3 }], customer: 'Lisa Wang', carrier: 'FedEx', tracking: 'FDX-7890123456', created_at: '1 day ago', address: 'Seattle, WA', delivered_at: '2026-03-05' },
        ],
      };
      setFulfillments(demo);
    } finally {
      setLoading(false);
    }
  };

  const advanceStatus = async (fulfillmentId, currentStatus) => {
    const nextMap = { pending: 'pick', picking: 'pack', packing: 'ship' };
    const next = nextMap[currentStatus];
    if (!next) return;

    try {
      const body = next === 'ship' ? { tracking_number: trackingNumber || 'AUTO-GENERATED' } : {};
      await apiClient.post(`/api/v1/fulfillments/${fulfillmentId}/${next}`, body);
      toast.success(`Fulfillment advanced to ${next}`);
      setTrackingNumber('');
      loadData();
    } catch (err) {
      toast.error('Failed to advance fulfillment');
    }
  };

  const columns = [
    { key: 'pending', label: 'Pending', icon: Clock, color: '#FFC857', count: fulfillments.pending.length },
    { key: 'picking', label: 'Picking', icon: ClipboardList, color: '#7A5CFF', count: fulfillments.picking.length },
    { key: 'packing', label: 'Packing', icon: Package, color: '#7A5CFF', count: fulfillments.packing.length },
    { key: 'shipped', label: 'Shipped', icon: Truck, color: '#3EF1C7', count: fulfillments.shipped.length },
    { key: 'delivered', label: 'Delivered', icon: CheckCircle, color: '#3EF1C7', count: fulfillments.delivered.length },
  ];

  const nextAction = { pending: 'Start Pick', picking: 'Mark Packed', packing: 'Ship It' };

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Fulfillment</h1>
            <p className="text-sm text-[#9CB0C7]">Pick - Pack - Ship</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">
              <Printer className="w-4 h-4" /> Print Labels
            </button>
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:text-white transition">Dashboard</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="flex gap-2 mb-6">
          {columns.map(col => (
            <div key={col.key} className="flex-1 bg-[#0E1426] border border-[#22304A] rounded-lg p-3 flex items-center gap-2">
              <col.icon className="w-4 h-4" style={{ color: col.color }} />
              <span className="text-sm text-white font-medium">{col.count}</span>
              <span className="text-xs text-[#9CB0C7]">{col.label}</span>
            </div>
          ))}
        </div>

        {/* Kanban Board */}
        <div className="flex gap-4 overflow-x-auto pb-4">
          {columns.map(col => (
            <div key={col.key} className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 mb-3 px-1">
                <col.icon className="w-4 h-4" style={{ color: col.color }} />
                <span className="text-sm text-white font-medium">{col.label}</span>
                <span className="text-xs text-[#9CB0C7] bg-[#0E1426] px-1.5 rounded">{col.count}</span>
              </div>
              <div className="space-y-3">
                {fulfillments[col.key].map(f => (
                  <div key={f.id} className="bg-[#0E1426] border border-[#22304A] rounded-xl p-4 hover:border-[#7A5CFF]/30 transition">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-[#7A5CFF] font-mono">{f.order_id}</span>
                      <span className="text-[10px] text-[#9CB0C7]">{f.created_at}</span>
                    </div>
                    <div className="text-sm text-white mb-1">{f.customer}</div>
                    <div className="text-xs text-[#9CB0C7] mb-2 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> {f.address}</div>

                    {/* Items */}
                    <div className="mb-3">
                      {f.items.map((item, i) => (
                        <div key={i} className="text-xs text-[#9CB0C7] flex items-center gap-1">
                          <Package className="w-2.5 h-2.5" /> {item.qty}x {item.title}
                        </div>
                      ))}
                    </div>

                    {/* Tracking */}
                    {f.tracking && (
                      <div className="text-xs text-[#3EF1C7] flex items-center gap-1 mb-2">
                        <Hash className="w-2.5 h-2.5" /> {f.tracking}
                        {f.eta && <span className="text-[#9CB0C7]"> - ETA: {f.eta}</span>}
                      </div>
                    )}

                    {/* Carrier */}
                    {f.carrier && <div className="text-xs text-[#9CB0C7] mb-2">Carrier: {f.carrier}</div>}

                    {/* Action Button */}
                    {nextAction[col.key] && (
                      <>
                        {col.key === 'packing' && (
                          <input value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} placeholder="Tracking #" className="w-full bg-[#0B1120] border border-[#22304A] rounded px-2 py-1 text-white text-xs mb-2" />
                        )}
                        <button onClick={() => advanceStatus(f.id, col.key)} className="w-full py-1.5 bg-[#7A5CFF]/10 text-[#7A5CFF] rounded text-xs font-medium hover:bg-[#7A5CFF]/20 transition flex items-center justify-center gap-1">
                          {nextAction[col.key]} <ArrowRight className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
                {fulfillments[col.key].length === 0 && (
                  <div className="text-center py-8 text-[#9CB0C7] text-xs">No items</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
